import { useMemo, useState, useEffect, useCallback } from "react";

import { type Collection } from "@shopify/hydrogen-react/storefront-api-types";
import useSWR from "swr/immutable";

import getCollectionByIdQuery from "@graphql/get-collection-by-id.graphql";
import { storefrontAPIFetcher } from "@react/hooks/fetchers";
import { COLLECTION_FILTER_IDS } from "@react/sections/Collection/CollectionFilters/constants";
import { useCollectionFilters } from "@react/sections/Collection/CollectionFiltersContext";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  formatCollectionContentCards,
  formatAdjacentCollections,
} from "@react/utilities/collection";
import { STOREFRONT_API_URL } from "@tablescape/constants";
import {
  type CollectionSectionSettings,
  type CollectionConnection,
  type OPProductConnection,
} from "@tablescape/state";

export const useCollectionParams = [STOREFRONT_API_URL, getCollectionByIdQuery];

export function useCollection(collectionId: Collection["id"] | undefined) {
  const {
    meta: {
      localization: { country, language },
    },
    pagination: { pageSize: defaultPageSize },
  } = useSectionSettings<CollectionSectionSettings>();

  const [cursor, setCursor] = useState<string | null>(null);
  const [productResults, setProductResults] = useState<OPProductConnection[]>(
    []
  );
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);

  const {
    appliedFilters,
    appliedSort,
    hasCompletedFirstRender,
    setAvailableFilters,
    setCollectionPriceMax,
  } = useCollectionFilters();

  const sortValue = useMemo(
    () => appliedSort?.value ?? undefined,
    [appliedSort?.value]
  );

  const filterInputs = useMemo(() => {
    return appliedFilters.map((f) => JSON.parse(String(f.input)));
  }, [appliedFilters]);

  const { data, isLoading } = useSWR<CollectionConnection>(
    collectionId && country
      ? [
          ...useCollectionParams,
          {
            collectionId,
            country,
            language,
            filters: filterInputs,
            pageSize,
            ...(cursor && { cursor }),
            ...(sortValue && {
              sortKey: sortValue.sortKey,
              reverse: sortValue.reverse,
            }),
          },
        ]
      : null,
    storefrontAPIFetcher
  );
  const collection = data?.data?.collection;
  const products = collection?.products;
  const hasNextPage = products?.pageInfo.hasNextPage;
  const endCursor = products?.pageInfo.endCursor;
  const fetchedPageSize = collection?.pageSize?.value;
  const pageOnScroll = collection?.pageOnScroll?.value;

  useEffect(() => {
    if (products?.filters) {
      setAvailableFilters(products.filters);
    }
  }, [products?.filters, setAvailableFilters]);

  useEffect(() => {
    const priceFilter = products?.filters?.find(
      (filter) => filter.id === COLLECTION_FILTER_IDS.PRICE
    );

    if (priceFilter?.values?.[0]?.input) {
      const parsed = JSON.parse(String(priceFilter.values[0].input));
      const max = parsed?.price?.max;
      if (typeof max === "number") {
        setCollectionPriceMax((prev) =>
          typeof prev === "number" && prev > 0 ? Math.max(prev, max) : max
        );
      }
    }
  }, [products?.filters, setCollectionPriceMax]);

  const filteredProducts = useMemo(
    () => products?.edges.map((edge) => edge.node) ?? [],
    [products?.edges]
  );

  useEffect(() => {
    if (fetchedPageSize) {
      setPageSize(parseInt(fetchedPageSize));
    }
  }, [fetchedPageSize]);

  useEffect(() => {
    setCursor(null);
    setProductResults([]);
  }, [filterInputs, sortValue]);

  useEffect(() => {
    setProductResults((prev) => [...prev, ...filteredProducts]);
  }, [filteredProducts]);

  const loadMore = useCallback(() => {
    setCursor(endCursor ?? null);
  }, [endCursor]);

  return {
    adjacentCollections: collection?.adjacentCollections
      ? formatAdjacentCollections(collection?.adjacentCollections)
      : [],
    breadcrumbs: collection?.breadcrumbs?.references.nodes ?? null,
    contentCards: collection?.contentCards?.references.nodes
      ? formatCollectionContentCards(collection?.contentCards?.references.nodes)
      : null,
    description:
      collection?.descriptionOverride?.value ?? collection?.description ?? null,
    handle: collection?.handle,
    hasNextPage,
    heroImage: collection?.heroImage?.reference.image,
    isLoading,
    isLoadingMore: isLoading && hasCompletedFirstRender,
    loadMore,
    pageOnScroll: pageOnScroll ? parseInt(pageOnScroll) : null,
    pageSize,
    productResults,
    sortOptions: collection?.sortOptions?.references.nodes ?? null,
    title: collection?.titleOverride?.value ?? collection?.title ?? null,
  };
}
